import { useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

import Heading from '@zenbusiness/zui/Heading';
import InputAdornment from '@zenbusiness/zui/InputAdornment';
import PropTypes from 'prop-types';
import { v4 as generateUuid } from 'uuid';

import useHideScroll from '#client/hooks/useHideScroll';
import { checkIsMobile } from '#client/utils/devices';
import {
  updateFormattedValue,
  updateTextColorByValue,
  formatInputValue,
  convertToCent,
  convertCentToUSDWithoutSymbol
} from '#client/utils/moneyInput';

import { TextInputStyled, BoxStyled } from './styles';

const MoneyInputBase = forwardRef(function MoneyInputBase(
  { isDisable = false, defaultValue, placeholder, error, onChangeValue, onBlur, autoFocus = true },
  ref
) {
  useHideScroll();
  const inputRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      getValue() {
        return convertToCent(inputRef.current.value);
      },
      focus() {
        inputRef.current.focus();
      }
    }),
    []
  );

  const handleChange = (event) => {
    const input = event.target;
    const formattedValue = formatInputValue(input.value);
    updateFormattedValue(input, formattedValue);
    updateTextColorByValue(input, formattedValue);
    if (typeof onChangeValue === 'function') {
      const valueInCent = convertToCent(formattedValue);
      onChangeValue(valueInCent);
    }
  };

  useEffect(() => {
    const input = inputRef.current;
    const isMobile = checkIsMobile();
    const handleTouch = (event) => {
      event.preventDefault();
      const end = input.value.length;

      input.setSelectionRange(end, end);
      input.focus();
    };

    const handleKeyDown = (event) => {
      // prevent user press arrow keys to move cursor, Home, End, PageUp, PageDown
      const keys = [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'PageUp',
        'PageDown'
      ];
      if (keys.includes(event.key)) {
        event.preventDefault();
      }
    };
    if (input) {
      updateTextColorByValue(input, input.value);
      if (!isMobile) {
        input.addEventListener('keydown', handleKeyDown);
        input.addEventListener('click', handleTouch);
        input.addEventListener('select', handleTouch);
      } else {
        input.addEventListener('touchstart', handleTouch);
        input.addEventListener('touchmove', handleTouch);
      }
    }

    return () => {
      if (input) {
        input.removeEventListener('touchstart', handleTouch);
        input.removeEventListener('touchmove', handleTouch);
        input.removeEventListener('keydown', handleKeyDown);
        input.removeEventListener('click', handleTouch);
        input.removeEventListener('select', handleTouch);
      }
    };
  }, []);

  return (
    <BoxStyled isDisable={isDisable}>
      <TextInputStyled
        autoFocus={autoFocus}
        defaultValue={convertCentToUSDWithoutSymbol(defaultValue)}
        disabled={isDisable}
        error={error}
        inputProps={{
          inputMode: 'numeric',
          'aria-label': 'Money input field',
          autoComplete: 'new-password',
          name: generateUuid()
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Heading
                color="primary"
                family="secondary"
                size="5xl"
                weight="bold"
              >
                <sup>$</sup>
              </Heading>
            </InputAdornment>
          )
        }}
        inputRef={inputRef}
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={placeholder}
        type="text"
        variant="standard"
      />
    </BoxStyled>
  );
});

MoneyInputBase.propTypes = {
  inputRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.node, PropTypes.any])
  }),
  placeholder: PropTypes.string,
  defaultValue: PropTypes.number,
  error: PropTypes.bool,
  onChangeValue: PropTypes.func,
  onBlur: PropTypes.func,
  isDisable: PropTypes.bool,
  autoFocus: PropTypes.bool
};

export default MoneyInputBase;
